import { useRef } from 'react'
import { createPortal } from 'react-dom'
import { useSightMap } from '@/hooks/useSightMap'
import { getConfig } from '@/data/config'
import { mapUnitToCardProps } from '@/data/mappers'
import { MapUnitPopup } from '@/components/MapView/MapUnitPopup'
import type { ApiUnit } from '@/types/api'

interface SightMapEmbedProps {
	sightmapUnitIds?: (string | number)[]
	locateUnitId?: string | number
	onUnitClick?: (unitId: string) => void
	mockUnits?: ApiUnit[]
	/**
	 * Alternate embeddable map URL supplied by the `view.change.before` hook.
	 * When set, this URL is loaded in a plain iframe and the SightMap Embed API
	 * (unit highlighting, tooltips) is skipped — the alternate map may not be a
	 * SightMap. `null`/undefined keeps the default SightMap embed behaviour.
	 */
	mapUrlOverride?: string | null
	/** Engrain floor ID for the custom floor picker; pushed to the embed via setFloor. */
	selectedFloorId?: string
	/** Fires when the embed reports a new active floor (e.g. after locateUnitById). */
	onFloorChange?: (floorId: string) => void
	/** Rounds all corners. Off when a header sits above the map in a parent card, so only the bottom corners round. */
	framed?: boolean
	/** Keep SightMap's building-image floor picker. Hidden by default. */
	showFloorPicker?: boolean
	className?: string
}

/**
 * Renders a SightMap iframe and initialises the SightMap Embed API.
 *
 * When `sightmapEmbedUrl` is not configured (dev mode / unconfigured asset),
 * renders a static placeholder instead.
 *
 * When `mapUrlOverride` is supplied, loads that URL in a plain iframe and skips
 * the SightMap Embed API entirely.
 */
export function SightMapEmbed ({ sightmapUnitIds = [], locateUnitId, onUnitClick, mockUnits, mapUrlOverride, selectedFloorId, onFloorChange, framed = true, showFloorPicker = false, className = '' }: SightMapEmbedProps) {
	const iframeRef = useRef<HTMLIFrameElement>(null)
	const sightmapEmbedUrl = getConfig().sightmapEmbedUrl
	const useOverride = typeof mapUrlOverride === 'string' && mapUrlOverride.length > 0

	// Skip SightMap Embed API init when an override URL is active by passing an
	// empty embed URL (the hook no-ops on a falsy URL).
	const { tooltipUnit, tooltipPos } = useSightMap(iframeRef, useOverride ? '' : sightmapEmbedUrl, sightmapUnitIds, locateUnitId, onUnitClick, mockUnits, selectedFloorId, onFloorChange, showFloorPicker)

	// Compute viewport-fixed coordinates for the tooltip portal.
	// SightMap events report mouseX/mouseY relative to the iframe; we offset
	// by the iframe's bounding rect to get viewport-fixed coordinates.
	let fixedX = 0
	let fixedY = 0
	if (tooltipUnit && tooltipPos && iframeRef.current) {
		const rect = iframeRef.current.getBoundingClientRect()
		fixedX = rect.left + tooltipPos.x
		fixedY = rect.top + tooltipPos.y
	}

	const radiusClass = framed ? 'spaces:rounded-24' : 'spaces:rounded-b-24'

	const portalTarget = typeof document !== 'undefined' ? document.getElementById('spaces-app') : null

	if (!sightmapEmbedUrl && !useOverride) {
		return (
			<div
				className={`spaces-sightmap-placeholder spaces:flex spaces:items-center spaces:justify-center spaces:bg-canvas-base ${radiusClass}${framed ? ' spaces:border spaces:border-canvas-3' : ''} ${className}`}
			>
				<div className='spaces:flex spaces:flex-col spaces:items-center spaces:gap-12 spaces:text-content-3'>
					<svg
						width='48'
						height='48'
						viewBox='0 0 24 24'
						fill='none'
						stroke='currentColor'
						strokeWidth='1.5'
						strokeLinecap='round'
						strokeLinejoin='round'
					>
						<path d='M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z' />
						<circle cx='12' cy='10' r='3' />
					</svg>
					<span className='spaces:text-14/24 spaces:font-default spaces:text-content-3'>SightMap will load here</span>
				</div>
			</div>
		)
	}

	const src = useOverride
		? (mapUrlOverride)
		: `${sightmapEmbedUrl}?enable_api=1&origin=${encodeURIComponent(window.location.origin)}`

	const tooltipPortal = tooltipUnit && tooltipPos && portalTarget
		? createPortal(
			<MapUnitPopup
				unit={mapUnitToCardProps(tooltipUnit)}
				x={fixedX}
				y={fixedY}
				fixed
			/>,
			portalTarget
		)
		: null

	return (
		<>
			{/* Older iOS WebKit won't clip an iframe to an ancestor's border-radius; redundant where it does. */}
			{/* Keyed on the floor picker: disableUI is one-way, so toggling it needs a fresh embed. The override iframe has no picker. */}
			<iframe
				key={useOverride ? 'override' : (showFloorPicker ? 'floor-picker' : 'no-floor-picker')}
				ref={iframeRef}
				title='Interactive property map'
				className={`spaces-sightmap${useOverride ? ' spaces-map-override' : ''} spaces:border-0 ${radiusClass} ${className}`}
				src={src}
				frameBorder='0'
				allow='geolocation; web-share; clipboard-write;'
			/>
			{tooltipPortal}
		</>
	)
}
